<template>
  <el-dialog
    title="Import log nạp tiền"
    :visible.sync="dialogVisible"
    width="70%"
    :before-close="handleClose"
    class="import-log-payment-modal"
  >
    <div class="import-content">
      <!-- File Upload Section -->
      <div class="upload-section">
        <div class="upload-header">
          <h4>Chọn file CSV để import</h4>
          <el-button
            type="text"
            @click="downloadTemplate"
            icon="el-icon-download"
          >
            Tải file mẫu
          </el-button>
        </div>
          <div class="upload-component">
            <UploadFile
              ref="uploadFile"
              inputId="log-payment-upload"
              @uploadFile="handleUploadFile"
              @deleteFile="handleDeleteFile"
            />
          </div>
      </div>

      <!-- Error Section for Record Limit -->
      <div v-if="previewData.length > 100" class="error-section">
        <el-alert
          title="File vượt quá số lượng bản ghi cho phép"
          type="error"
          :closable="false"
          show-icon
          description="Bạn chỉ được import tối đa 100 bản ghi. Vui lòng chia nhỏ file trước khi import."
        />
      </div>

      <!-- Preview Section -->
      <div v-else-if="previewData.length > 0" class="preview-section">
        <h4>Preview dữ liệu ({{ previewData.length }} dòng):</h4>
        <PreviewTable
          :data="previewData"
          :rules="logPaymentSchema"
          :labels="labels"
          :external-errors="apiErrors"
          @validated="handleValidated"
        />
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose" :disabled="isImporting">Hủy</el-button>
      <el-button
        type="primary"
        @click="handleImport"
        :loading="isImporting"
        :disabled="!canImport"
      >
        {{ isImporting ? "Đang import..." : "Import" }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import UploadFile from "@/components/UploadFile.vue";
import PreviewTable from "@/components/PreviewTable";
import { mapGetters } from "vuex";
import { IMPORT_DEPOSIT_LOG } from "@/core/services/store/contract/studentAmount.module";
import { VALIDATE_CONTRACTS_IMPORT } from "@/core/services/store/contract/contracts.module";
import { LogPaymentImportInputDTO, LogPaymentImportOutputDTO, LogPaymentValidationErrorDTO, LogPaymentApiPayloadDTO } from "@/core/dto/logPayment.dto";
import { PaymentMethodEnum, CardPurposeEnum, CardPurposeLabel, RecipientAccountLabel, FeePaymentMethodLabel } from "@/core/enums/logPayment.enum";
import { logPaymentSchema } from "@/core/schemas/logPayment.validation.schema";

export default {
  name: "ImportLogPaymentModal",
  components: {
    UploadFile,
    PreviewTable
  },
  props: {
    /**
     * Hiển thị modal import
     * @type {Boolean}
     * @default false
     */
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialogVisible: false,
      previewData: [],
      validationErrors: [],
      isValid: true,
      isImporting: false,
      convertedData: [],
      logPaymentSchema, // Import schema từ file
      PaymentMethodEnum,
      CardPurposeEnum,
      // Mapping tên field/cột để hiển thị tiếng Việt
      labels: {
        rowIndex: 'STT',
        customer_phone: 'Số điện thoại',
        contract_id: 'Mã hợp đồng',
        amount: 'Số tiền nạp',
        payment_method: 'Hình thức thanh toán',
        recipient_account: 'Tài khoản nhận tiền',
        card_purpose: 'Mục đích quẹt thẻ',
        fee_payment_method: 'Hình thức thanh toán phí'
      },
      apiErrors: []
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    recipientAccountLabel() { return RecipientAccountLabel; },
    cardPurposeLabel() { return CardPurposeLabel; },
    feePaymentMethodLabel() { return FeePaymentMethodLabel; },
    canImport() {
      return (
        this.previewData.length > 0 &&
        this.previewData.length <= 100 &&
        this.isValid &&
        !this.isImporting
      );
    },
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal;
      if (newVal) {
        // Reset khi mở modal
        this.resetModalState();
      }
    },
    dialogVisible(newVal) {
      if (!newVal) {
        this.$emit("close");
      }
    },
  },
  methods: {
    /**
     * Đóng modal
     */
    handleClose() {
      this.dialogVisible = false;
    },

    /**
     * Reset trạng thái modal
     */
    resetModalState() {
      this.previewData = [];
      this.validationErrors = [];
      this.isValid = true;
      this.isImporting = false;
      this.convertedData = [];
      // Reset UploadFile component
      this.$nextTick(() => {
        if (this.$refs.uploadFile) {
          this.$refs.uploadFile.resetData();
        }
      });
    },

    /**
     * Xử lý khi upload file thành công
     * @param {Array} data - Dữ liệu từ CSV
     */
    handleUploadFile(data) {
      if (!data || !Array.isArray(data)) {
        this.$notify({
          title: "Lỗi",
          message: "Dữ liệu không hợp lệ",
          type: "error",
        });
        return;
      }
      
      // Sử dụng DTO để chuẩn hóa dữ liệu
      this.convertedData = LogPaymentImportInputDTO(data);
      this.previewData = this.convertedData;
      // PreviewTable sẽ tự động validate khi data thay đổi
      this.validateWithApi();
    },

    /**
     * Xử lý khi xóa file
     */
    handleDeleteFile() {
      this.resetModalState();
    },

    /**
     * Gọi API validate-import (hợp đồng) cho log nạp tiền để đối soát contract/phone
     */
    async validateWithApi() {
      if (!this.previewData || this.previewData.length === 0) {
        this.apiErrors = [];
        return;
      }
      try {
        const records = this.previewData.map((row, idx) => ({
          rowIndex: row.rowIndex || idx + 1,
          contract_id: typeof row.contract_id === 'number' ? row.contract_id : parseInt(String(row.contract_id).replace(/[^0-9]/g, ''), 10),
          customer_phone: row.customer_phone != null ? String(row.customer_phone).trim() : ''
        }));
        const payload = { records };
        const data = await this.$store.dispatch(VALIDATE_CONTRACTS_IMPORT, payload);
        const apiErrors = (data && data.data && Array.isArray(data.data.errors)) ? data.data.errors : [];
        this.apiErrors = apiErrors.map(e => ({
          row: e.row || e.rowIndex || 0,
          field: e.field,
          message: e.message
        }));
      } catch (error) {
        this.apiErrors = [];
        console.warn('API validate-import lỗi', error);
      }
    },

    /**
     * Xử lý kết quả validation từ PreviewTable
     * @param {Object} result - Kết quả validation { errors, errorMap }
     */
        handleValidated({ errors, isValid }) {
          this.validationErrors = errors || [];
          this.isValid = isValid;
        },

    /**
     * Thực hiện import log payment
     */
    async handleImport() {
      if (!this.canImport) {
        return;
      }

      this.isImporting = true;

      try {
        const payload = LogPaymentApiPayloadDTO(this.convertedData);

        const response = await this.$store.dispatch(IMPORT_DEPOSIT_LOG, payload);
        
        const normalizedResponse = LogPaymentImportOutputDTO(response);
        
        if (normalizedResponse.errors && normalizedResponse.errors.length > 0) {
          this.handleImportErrors(normalizedResponse.errors);
        } else {
          this.$notify({
            title: "Thành công",
            message: `Import thành công ${normalizedResponse.successCount || this.convertedData.length} bản ghi`,
            type: "success",
          });
          this.$emit("import-success");
        }

      } catch (error) {
        const resp = error && error.response ? error.response : null;
        const data = resp ? resp.data : (error && error.data ? error.data : null);
      
        if (data && data.error && Array.isArray(data.errors)) {
          // Format: {error: true, errors: [{rowIndex: 1, message: "..."}]}
          this.handleImportErrors(data.errors);
        } else if (data && Array.isArray(data.errors)) {
          // Format: {errors: [{rowIndex: 1, message: "..."}]}
          this.handleImportErrors(data.errors);
        } else if (data && data.data && Array.isArray(data.data.errors)) {
          // Fallback: errors lồng trong data.errors
          this.handleImportErrors(data.data.errors);
        } else if (data && data.message) {
          // General error message
          this.$notify({
            title: "Lỗi",
            message: data.message,
            type: "error",
          });
        } else {
          // Fallback cho lỗi thông thường
          this.$notify({
            title: "Lỗi",
            message: "Có lỗi xảy ra khi import",
            type: "error",
          });
        }
      } finally {
        this.isImporting = false;
      }
    },

    /**
     * Xử lý lỗi từ API
     * @param {Array} errors - Danh sách lỗi từ API
     */
    handleImportErrors(errors) {
      
      // Convert structured errors to display format
      const displayErrors = [];
      
      if (Array.isArray(errors)) {
        errors.forEach(errorData => {
          // Handle different error formats from API
          if (errorData.rowIndex && errorData.errors) {
            // Structured format: {rowIndex: 1, errors: {field: "message"}}
            const { rowIndex, errors: fieldErrors } = errorData;
            Object.keys(fieldErrors).forEach(field => {
              displayErrors.push(LogPaymentValidationErrorDTO({
                row: rowIndex,
                field,
                message: fieldErrors[field]
              }));
            });
          } else if (errorData.rowIndex && errorData.message) {
            // Simple format: {rowIndex: 1, message: "error message"}
            displayErrors.push(LogPaymentValidationErrorDTO({
              row: errorData.rowIndex,
              field: 'general',
              message: errorData.message
            }));
          } else if (errorData.message) {
            // General error format: {message: "error message"}
            displayErrors.push(LogPaymentValidationErrorDTO({
              row: 1,
              field: 'general',
              message: errorData.message
            }));
          }
        });
      }

          this.validationErrors = displayErrors;
          this.apiErrors = displayErrors;

      // Hiển thị thông báo tổng quan
      const totalErrors = displayErrors.length;
      this.$notify({
        title: "Import hoàn thành với lỗi",
        message: `Có ${totalErrors} lỗi. Vui lòng kiểm tra và sửa lại.`,
        type: "warning",
      });
    },

    /**
     * Tải file mẫu
     */
    downloadTemplate() {
      const link = document.createElement("a");
      link.href = "/excel/File mẫu import log nạp tiền.csv";
      link.download = "File mẫu import log nạp tiền.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    /**
     * Format giá tiền để hiển thị
     * @param {Number} amount - Số tiền
     * @returns {String}
     */
    formatPrice(amount) {
      return formatLogPaymentPrice(amount);
    },

    /**
     * Lấy label của field để hiển thị
     * @param {String} field - Tên field
     * @returns {String}
     */
  },
};
</script>

<style scoped>
.import-log-payment-modal {
  max-height: none;
}

.import-content {
  max-height: none;
  overflow: visible;
}

.upload-section {
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.upload-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.upload-header h4 {
  margin: 0;
  color: #495057;
  font-size: 16px;
  font-weight: 600;
}

.upload-component {
  margin-top: 12px;
}

.preview-section {
  margin-bottom: 24px;
}

.preview-section h4 {
  margin: 0 0 16px 0;
  color: #495057;
  font-size: 16px;
  font-weight: 600;
}

/* Table scroll styling */
.preview-section .el-table {
  max-height: 400px;
  overflow-y: auto;
}

.preview-section .el-table__body-wrapper {
  max-height: 350px;
  overflow-y: auto;
}


.dialog-footer {
  text-align: right;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

/* Responsive */
@media (max-width: 768px) {
  .import-log-payment-modal {
    width: 95% !important;
  }

  .upload-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .upload-header h4 {
    margin-bottom: 8px;
  }
}
</style>
